import React, { useRef, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { tokens, fonts } from '../theme';
import site, { usePageMeta } from '../content';
import Faq from './Faq';

const ACCEPTED = '.pdf,.docx,.pptx';
const home = site.home;

const Section = ({ id, title, children }) => (
  <Box component="section" id={id} aria-labelledby={id && `${id}-title`} sx={{ borderTop: `1px solid ${tokens.line}`, py: { xs: 7, md: 10 } }}>
    <Container maxWidth="lg">
      {title && (
        <Typography variant="h2" component="h2" id={id && `${id}-title`} sx={{ mb: 5 }}>
          {title}
        </Typography>
      )}
      {children}
    </Container>
  </Box>
);

// One orchestrated moment: the old logo on each page swaps to the new one, page by page.
const fadeOut = keyframes`to { opacity: 0; transform: translateY(-6px); }`;
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;

const DocPage = ({ index, sx }) => {
  const delay = 0.9 + index * 0.35;
  return (
    <Box
      sx={{
        position: 'absolute',
        width: { xs: 220, sm: 260 },
        aspectRatio: '1 / 1.3',
        backgroundColor: tokens.raised,
        border: `1px solid ${tokens.line}`,
        borderRadius: 2,
        p: 2.5,
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.35)',
        ...sx,
      }}
    >
      {/* Logo slot */}
      <Box sx={{ position: 'relative', height: 28, mb: 3 }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: tokens.muted,
            animation: `${fadeOut} 0.4s ease ${delay}s forwards`,
          }}
        >
          <Box sx={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${tokens.muted}` }} />
          <Box sx={{ fontFamily: 'Georgia, serif', fontSize: 15, fontStyle: 'italic' }}>Oldco</Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            opacity: 0,
            animation: `${fadeIn} 0.45s ease ${delay + 0.2}s forwards`,
          }}
        >
          <Box sx={{ width: 22, height: 22, borderRadius: '6px', backgroundColor: tokens.accent }} />
          <Box sx={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 15, color: tokens.text }}>Newco</Box>
        </Box>
      </Box>
      {/* Text lines */}
      {[92, 100, 78, 96, 64].map((w, i) => (
        <Box key={i} sx={{ height: 7, width: `${w}%`, borderRadius: 4, backgroundColor: tokens.line, mb: 1.25 }} />
      ))}
      <Box sx={{ height: 64, borderRadius: 1.5, backgroundColor: tokens.surface, mt: 2.5 }} />
    </Box>
  );
};

const DocStack = () => (
  <Box
    aria-hidden="true"
    sx={{ position: 'relative', height: { xs: 330, sm: 400 }, width: '100%', maxWidth: 420, mx: 'auto' }}
  >
    <DocPage index={0} sx={{ top: 0, left: 0, transform: 'rotate(-4deg)', opacity: 0.55 }} />
    <DocPage index={1} sx={{ top: 24, left: { xs: 40, sm: 70 }, transform: 'rotate(1.5deg)', opacity: 0.8 }} />
    <DocPage index={2} sx={{ top: 52, left: { xs: 80, sm: 140 }, transform: 'rotate(5deg)' }} />
  </Box>
);

function LandingPage() {
  usePageMeta(home);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const startWith = (file) => navigate('/upload', { state: file ? { file } : undefined });

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) startWith(file);
  };

  return (
    <>
      {/* Hero */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 11 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
            alignItems: 'center',
            gap: { xs: 6, md: 8 },
          }}
        >
          <Box>
            <Typography variant="h1" component="h1" sx={{ maxWidth: 620 }}>
              {home.h1}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2.5, maxWidth: 480, fontSize: '1.125rem' }}>
              {home.intro}
            </Typography>

            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                mt: 5,
                p: { xs: 2.5, sm: 3 },
                maxWidth: 520,
                border: `1.5px dashed ${dragOver ? tokens.accent : tokens.line}`,
                borderRadius: 3,
                backgroundColor: dragOver ? 'rgba(140, 158, 255, 0.08)' : tokens.surface,
                transition: 'border-color 0.15s, background-color 0.15s',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<UploadFileIcon />}
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                Upload a document
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {dragOver ? 'Drop to start' : 'or drop a file here'}
              </Typography>
              <input
                ref={inputRef}
                type="file"
                hidden
                accept={ACCEPTED}
                onChange={(e) => e.target.files && e.target.files[0] && startWith(e.target.files[0])}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Free, no sign-up needed. Files are deleted after download.
            </Typography>
          </Box>

          <DocStack />
        </Box>
      </Container>

      {/* What is RepLogo — a plain definition for readers, search snippets and AI answers */}
      <Section id="what-is-replogo" title="What is RepLogo?">
        <Typography sx={{ maxWidth: 760, fontSize: '1.125rem', color: tokens.text }}>
          {home.definition}
        </Typography>
      </Section>

      {/* How it works */}
      <Section id="how-it-works" title={home.stepsTitle}>
        <Box
          component="ol"
          sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: { xs: 4, md: 5 },
          }}
        >
          {home.steps.map((step, i) => (
            <Box component="li" key={step.title} sx={{ borderTop: `2px solid ${i === 0 ? tokens.accent : tokens.line}`, pt: 2.5 }}>
              <Typography sx={{ fontFamily: fonts.display, fontWeight: 700, color: tokens.accent, mb: 1 }}>
                {i + 1}
              </Typography>
              <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {/* Supported file types */}
      <Section id="file-types" title={home.formatsTitle}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              minWidth: 520,
              borderCollapse: 'collapse',
              '& th, & td': { textAlign: 'left', py: 2, pr: 3, borderBottom: `1px solid ${tokens.line}` },
              '& th': { color: tokens.muted, fontWeight: 500, fontSize: '0.875rem' },
              '& td': { color: tokens.text },
            }}
          >
            <thead>
              <tr><th scope="col">File type</th><th scope="col">Extension</th><th scope="col">Where the logo is replaced</th></tr>
            </thead>
            <tbody>
              {site.formats.map((f) => (
                <tr key={f.path}>
                  <td>
                    <Box component={RouterLink} to={f.path} sx={{ color: tokens.accentStrong, fontWeight: 600 }}>
                      {f.name}
                    </Box>
                  </td>
                  <td>{f.extension}</td>
                  <td>{f.where}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Files up to {site.maxFileSizeMb} MB.
        </Typography>
      </Section>

      {/* Who it's for */}
      <Section>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: { xs: 3, md: 8 } }}>
          <Typography variant="h2" component="h2">
            {home.audiencesTitle}
          </Typography>
          <Box
            component="ul"
            sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 5 }}
          >
            {home.audiences.map((a) => (
              <Box component="li" key={a} sx={{ py: 2, borderBottom: `1px solid ${tokens.line}`, fontSize: '1.0625rem' }}>
                {a}
              </Box>
            ))}
          </Box>
        </Box>
      </Section>

      {/* FAQ */}
      <Section id="faq" title={home.faqTitle}>
        <Faq items={home.faq} />
      </Section>

      {/* Closing call to action */}
      <Box sx={{ borderTop: `1px solid ${tokens.line}`, py: { xs: 7, md: 9 } }}>
        <Container
          maxWidth="lg"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}
        >
          <Typography variant="h2" component="h2" sx={{ maxWidth: 560 }}>
            {home.ctaTitle}
          </Typography>
          <Button variant="contained" size="large" onClick={() => startWith(null)}>
            {home.ctaButton}
          </Button>
        </Container>
      </Box>
    </>
  );
}

export default LandingPage;
